import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Roles, useAuth } from '../../app/providers';
import { useResource } from '../../lib/hooks';
import {
  EmptyState, PageHeader, Panel, SectionTitle, SkeletonLines, Stat, Stats,
} from '../../components/PageHeader';
import {
  IconAlert, IconArrowRight, IconCourses, IconStaff, IconStudents, IconTeacher, NAV_ICONS,
} from '../../components/Icons';
import { MONTH_OPTIONS } from '../../components/FilterBar';

// ---------------------------------------------------------------------------
// Payload
// ---------------------------------------------------------------------------

/**
 * `/api/dashboard/summary`, exactly as the server returns it.
 *
 * Every section is optional because the server omits whatever the signed-in role may not
 * see. The page renders what arrived and nothing else - it never decides for itself which
 * role gets what, so the screen and the server cannot disagree.
 */
type Summary = {
  role?: string;
  headcount?: Headcount;
  feesByMonth?: FeesProgress[];
  registrations?: MonthCount[];
  feesSubmissionsThisMonth?: DayCount[];
};

type Headcount = {
  students: number;
  teachers: number;
  staff: number;
  courses: number;
};

type FeesProgress = {
  month: number;
  year: number;
  paidCount: number;
  partialCount: number;
  dueCount: number;
  lineCount: number;
  paidPercent: number;
};

type MonthCount = { month: number; year: number; count: number };
type DayCount = { day: number; count: number };

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

const pct = (value: number) => `${value.toFixed(1)}%`;

const ordinal = (day: number) => {
  const tens = day % 100;
  const suffix =
    tens >= 11 && tens <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][day % 10] ?? 'th';
  return `${day}${suffix}`;
};

const monthName = (month: number) =>
  MONTH_OPTIONS.find((option) => option.value === month)?.label ?? '';

const shortMonth = (month: number) => monthName(month).slice(0, 3);

// ---------------------------------------------------------------------------
// Fee collection
// ---------------------------------------------------------------------------

/**
 * Six months of fee collection, one compact row each, newest first.
 *
 * One row per month rather than one card per month: the six are the same measurement at
 * different times, and stacking them on a shared left edge is what lets you see the shape
 * of the last half-year without reading a single number.
 *
 * Each bar is split the way the All Fees grid is split, and colour never carries meaning
 * alone - the legend names the three states once at the top, the settled percentage is
 * printed on every row, and the exact counts are in each row's tooltip.
 */
function FeesByMonth({ months }: { months: FeesProgress[] }) {
  const segments = [
    { key: 'paid', label: 'Paid' },
    { key: 'partial', label: 'Partial' },
    { key: 'due', label: 'Due' },
  ] as const;

  const countOf = (month: FeesProgress, key: (typeof segments)[number]['key']) =>
    key === 'paid' ? month.paidCount : key === 'partial' ? month.partialCount : month.dueCount;

  return (
    <div className="fees">
      <div className="fees__legend">
        {segments.map((segment) => (
          <span key={segment.key} className="fees__key">
            <i className={`fees__dot fees__dot--${segment.key}`} />
            {segment.label}
          </span>
        ))}
      </div>

      {months.map((month, index) => (
        <div
          className={index === 0 ? 'fees__row fees__row--now' : 'fees__row'}
          key={`${month.year}-${month.month}`}
          title={
            month.lineCount === 0
              ? `${monthName(month.month)} ${month.year}: nothing billed`
              : `${monthName(month.month)} ${month.year}: ${month.paidCount} paid, ` +
                `${month.partialCount} partial, ${month.dueCount} due of ${month.lineCount}`
          }
        >
          <span className="fees__month">
            {shortMonth(month.month)}
            <small>{String(month.year).slice(2)}</small>
          </span>

          <span className="fees__track">
            {month.lineCount > 0 &&
              segments.map((segment) => {
                const count = countOf(month, segment.key);

                return count > 0 ? (
                  <i
                    key={segment.key}
                    className={`fees__seg fees__seg--${segment.key}`}
                    style={{ width: `${(count / month.lineCount) * 100}%` }}
                  />
                ) : null;
              })}
          </span>

          <span className="fees__pct">
            {month.lineCount === 0 ? '—' : pct(month.paidPercent)}
          </span>

          <span className="fees__lines">{month.lineCount}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Registrations
// ---------------------------------------------------------------------------

/**
 * A year of student registrations, one column per month.
 *
 * Columns rather than a line, because each month is a discrete count rather than a reading
 * from something continuous - a line between March and April would imply values in between
 * that do not exist.
 *
 * Plain flexbox rather than a charting dependency, for the same reason the icons are
 * hand-rolled: this is twelve rectangles, and a library would add a bundle, a licence and a
 * second palette that has to be kept in step with the theme's own.
 */
function RegistrationChart({ months, periodLabel }: { months: MonthCount[]; periodLabel: string }) {
  const peak = Math.max(1, ...months.map((m) => m.count));
  const total = months.reduce((sum, m) => sum + m.count, 0);

  return (
    <div className="mchart">
      <div className="mchart__legend">
        <span>
          <b>{total}</b> registered {periodLabel}
        </span>
        <span className="mchart__peak">Busiest {peak}</span>
      </div>

      <div className="mchart__plot">
        {months.map((month) => (
          <div
            className="mchart__col"
            key={`${month.year}-${month.month}`}
            title={`${monthName(month.month)} ${month.year}: ${month.count} registered`}
          >
            <span className="mchart__value">{month.count}</span>

            <span className="mchart__bar-wrap">
              {/* A month with nobody still needs a mark on the baseline: an absent column
                  reads as missing data, and a quiet month is not the same thing. */}
              <span
                className={month.count === 0 ? 'mchart__bar mchart__bar--empty' : 'mchart__bar'}
                style={{ height: `${Math.max(2, (month.count / peak) * 100)}%` }}
              />
            </span>

            <span className="mchart__tick">
              {shortMonth(month.month)}
              {month.month === 1 && <small>{String(month.year).slice(2)}</small>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fee submissions this month
// ---------------------------------------------------------------------------

/**
 * This month's fee submissions, one point per calendar day.
 *
 * A line rather than columns: the days of one month are a run of readings on the same axis,
 * and the shape across the month - the rush once fees are generated, the mid-month lull - is
 * the thing worth seeing at a glance. It counts submissions and never the money on them.
 *
 * One inline SVG, no charting dependency, for the reason the rest of this file gives: it is
 * a polyline and a handful of ticks, and a library would add a bundle and a second palette
 * to keep in step with the theme. Strokes use non-scaling-stroke so the line keeps its
 * weight when the SVG is scaled to the panel width.
 */
function SubmissionsThisMonth({ days, periodLabel }: { days: DayCount[]; periodLabel: string }) {
  const total = days.reduce((sum, d) => sum + d.count, 0);
  const peak = Math.max(1, ...days.map((d) => d.count));
  const busiest = days.reduce((best, d) => (d.count > best.count ? d : best), days[0]);

  // Fixed user-space box. The SVG is pinned to a fixed pixel height in CSS and stretched to
  // the panel width (preserveAspectRatio="none"), so this box is kept close to the rendered
  // proportions - a wide viewBox - to keep that stretch small enough to leave dots round and
  // tick labels unwarped.
  const width = 1280;
  const height = 190;
  const padX = 10;
  const padTop = 12;
  const padBottom = 26;
  const plotW = width - padX * 2;
  const plotH = height - padTop - padBottom;
  const baseline = padTop + plotH;

  const n = days.length;
  const xAt = (index: number) => padX + (n === 1 ? plotW / 2 : (index / (n - 1)) * plotW);
  const yAt = (count: number) => baseline - (count / peak) * plotH;

  const points = days.map((d, i) => `${xAt(i)},${yAt(d.count)}`).join(' ');
  const areaPoints = `${padX},${baseline} ${points} ${padX + plotW},${baseline}`;

  // The 1st, the last, and every fifth day in between - enough to place a point without
  // printing thirty-one labels under a 640-wide box.
  const ticks = days
    .map((d) => d.day)
    .filter((day) => day === 1 || day === n || (day % 5 === 0 && n - day >= 3));

  return (
    <div className="dchart">
      <div className="dchart__legend">
        <span>
          <b>{total}</b> submitted {periodLabel}
        </span>
        {total > 0 && (
          <span className="dchart__peak">
            Busiest {busiest.count} on the {ordinal(busiest.day)}
          </span>
        )}
      </div>

      <svg
        className="dchart__plot"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Fee submissions per day ${periodLabel}, ${total} in total`}
      >
        <line
          className="dchart__axis"
          x1={padX}
          y1={baseline}
          x2={padX + plotW}
          y2={baseline}
        />

        <polygon className="dchart__area" points={areaPoints} />
        <polyline className="dchart__line" points={points} />

        {days.map((d, i) => (
          <circle
            key={d.day}
            className={d.count === 0 ? 'dchart__dot dchart__dot--zero' : 'dchart__dot'}
            cx={xAt(i)}
            cy={yAt(d.count)}
            r={4}
          >
            <title>
              {ordinal(d.day)}: {d.count} {d.count === 1 ? 'submission' : 'submissions'}
            </title>
          </circle>
        ))}

        {ticks.map((day) => (
          <text
            key={day}
            className="dchart__tick"
            x={xAt(day - 1)}
            y={height - 5}
            textAnchor="middle"
          >
            {day}
          </text>
        ))}
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shortcuts
// ---------------------------------------------------------------------------

type Tile = { to: string; label: string; hint: string; group: string; roles: string[] };

const TILES: Tile[] = [
  { to: '/fees/submission', label: 'Collect fees', hint: 'Take a payment from a student', group: 'Money in', roles: [Roles.Director, Roles.Cashier] },
  { to: '/fees', label: 'All fees', hint: 'Browse every fee line', group: 'Money in', roles: [Roles.Director, Roles.Cashier] },
  { to: '/fees/slips', label: 'Fees slips', hint: 'Receipts already issued', group: 'Money in', roles: [Roles.Director, Roles.Cashier] },
  { to: '/fees/search', label: 'Advanced search', hint: 'Collected vs outstanding', group: 'Money in', roles: [Roles.Director, Roles.Cashier] },
  { to: '/salary/teachers', label: 'Teacher salary', hint: 'Share-based monthly pay', group: 'Money out', roles: [Roles.Director] },
  { to: '/salary/staff', label: 'Staff salary', hint: 'Flat monthly pay', group: 'Money out', roles: [Roles.Director] },
  { to: '/expenses', label: 'Expenses', hint: 'Record and review spending', group: 'Money out', roles: [Roles.Director, Roles.Cashier] },
  { to: '/students', label: 'Students', hint: 'Register and manage students', group: 'Records', roles: [Roles.Director, Roles.Coordinator] },
  { to: '/courses', label: 'Courses', hint: 'Courses, schedules and rosters', group: 'Records', roles: [Roles.Director, Roles.Coordinator] },
  { to: '/tests', label: 'Tests', hint: 'Score sheets in and out', group: 'Records', roles: [Roles.Director, Roles.Coordinator] },
];

const TILE_GROUPS = ['Money in', 'Money out', 'Records'];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

/**
 * The landing page: what the numbers say first, then where to act on them.
 *
 * The order is deliberate. Someone opening this wants to know where the month stands before
 * deciding what to click, so the figures lead and the shortcuts - which people liked and
 * which are kept in full - close the page.
 *
 * Everything above the shortcuts is server-gated. A Coordinator's payload carries no fees
 * and a Cashier's carries no registers, so those sections simply do not render; there is no
 * client-side role test deciding what to hide, and therefore nothing that can drift out of
 * step with the API's own [Authorize] rules.
 *
 * The data sections carry no heading band of their own - each panel's own title says what it
 * holds, and a band repeating that above it was a second label for the same thing.
 */
/** Zero-padded "YYYY-MM", the value shape `<input type="month">` reads and writes. */
const toMonthInputValue = (year: number, month: number) =>
  `${year}-${String(month).padStart(2, '0')}`;

/**
 * A month input whose picker opens on a click anywhere in the field, not only on the
 * calendar glyph at its right edge. Native `<input type="month">` only reacts to the glyph,
 * which leaves most of the control looking clickable but doing nothing.
 */
/**
 * Holds a chart's footprint steady while its panel refetches after a filter change.
 *
 * `useResource` leaves the previous payload mounted during a reload, so the chart is still
 * drawn underneath at its normal size and the panel keeps its height. This only veils it -
 * a light blur, dimmed, no longer interactive - and floats a note, so changing the period
 * reads as a refresh in place rather than the chart collapsing and springing back open.
 */
function ChartArea({ busy, children }: { busy: boolean; children: ReactNode }) {
  return (
    <div className={busy ? 'chartwrap chartwrap--busy' : 'chartwrap'} aria-busy={busy}>
      {children}
      {busy && (
        <span className="chartwrap__note" role="status" aria-label="Updating chart">
          <span className="chartwrap__spin" />
        </span>
      )}
    </div>
  );
}

function MonthPicker({
  label,
  value,
  max,
  onChange,
}: {
  label: string;
  value: string;
  max?: string;
  onChange: (value: string) => void;
}) {
  return (
    <input
      type="month"
      className="input"
      style={{ width: 160 }}
      aria-label={label}
      value={value}
      max={max}
      onClick={(e) => {
        // showPicker() needs a user gesture and throws if the browser blocks it or lacks
        // it entirely; on failure the native glyph click still works.
        try {
          e.currentTarget.showPicker();
        } catch {
          /* fall back to native behaviour */
        }
      }}
      onChange={(e) => e.target.value && onChange(e.target.value)}
    />
  );
}

export function DashboardPage() {
  const { user, hasRole } = useAuth();

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentPeriod = toMonthInputValue(currentYear, currentMonth);

  // Headcount always covers today; these three charts are the only ones with a period of
  // their own to pick, so they are the only state this screen holds.
  const [registrationsYear, setRegistrationsYear] = useState(currentYear);
  const [feesPeriod, setFeesPeriod] = useState(currentPeriod);
  const [submissionsPeriod, setSubmissionsPeriod] = useState(currentPeriod);

  const [feesYearStr, feesMonthStr] = feesPeriod.split('-');
  const feesYear = Number(feesYearStr) || currentYear;
  const feesMonth = Number(feesMonthStr) || currentMonth;

  const [submissionsYearStr, submissionsMonthStr] = submissionsPeriod.split('-');
  const submissionsYear = Number(submissionsYearStr) || currentYear;
  const submissionsMonth = Number(submissionsMonthStr) || currentMonth;

  const summary = useResource<Summary>(
    `/api/dashboard/summary?registrationsYear=${registrationsYear}` +
      `&feesYear=${feesYear}&feesMonth=${feesMonth}` +
      `&submissionsYear=${submissionsYear}&submissionsMonth=${submissionsMonth}`,
  );

  const data = summary.data;
  const tiles = TILES.filter((tile) => hasRole(...(tile.roles as never[])));

  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const fees = data?.feesByMonth;
  const registrations = data?.registrations;
  const submissions = data?.feesSubmissionsThisMonth;

  // Both charts are month-by-month views and share a row. Which of them a role gets is the
  // server's decision, so the row simply renders whichever arrived; the grid gives one panel
  // the full width and two a half each, without either case being spelled out here.
  const hasFees = Boolean(fees?.length);
  const hasRegistrations = Boolean(registrations?.length);

  // A reload with a payload already on screen - a filter change, not the first visit. The
  // charts stay where they are and get veiled in place; the full-page skeleton is only for
  // the cold load, when there is nothing to keep steady.
  const refetching = summary.loading && Boolean(data);

  const feesIsCurrent = feesPeriod === currentPeriod;
  const feesPeriodName = `${monthName(feesMonth)} ${feesYear}`;
  const feesTitle = `Fee collection, ${feesIsCurrent ? 'last 6 months' : `6 months ending ${feesPeriodName}`}`;

  const registrationsIsRolling = registrationsYear === currentYear;
  const registrationsTitle = `New students, ${registrationsIsRolling ? 'last 12 months' : registrationsYear}`;
  const registrationsPeriodLabel = registrationsIsRolling
    ? 'in the last 12 months'
    : `in ${registrationsYear}`;

  const submissionsIsCurrent = submissionsPeriod === currentPeriod;
  const submissionsPeriodName = `${monthName(submissionsMonth)} ${submissionsYear}`;
  const submissionsTitle =
    `Fee submissions ${submissionsIsCurrent ? 'this month' : `in ${submissionsPeriodName}`}, by day`;
  const submissionsPeriodLabel = submissionsIsCurrent ? 'this month' : `in ${submissionsPeriodName}`;

  // The picker never offers a future month - there is nothing to show yet, and generation
  // for a month that has not started cannot have run.
  const registrationYearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <>
      <PageHeader
        title={`${greeting}, ${user?.userName ?? ''}`}
        subtitle={data?.role ? `${data.role} dashboard` : 'Loading your dashboard…'}
      />

      <div className="dash">
        {summary.loading && !data && (
          <div className="card">
            <SkeletonLines lines={4} />
          </div>
        )}

        {summary.error && !summary.loading && (
          <div className="card">
            <div className="row" style={{ gap: 'var(--sp-3)', alignItems: 'center' }}>
              <IconAlert />
              <span className="grow">Could not load the dashboard figures. {summary.error}</span>
              <button type="button" className="btn btn--sm" onClick={() => void summary.reload()}>
                Retry
              </button>
            </div>
          </div>
        )}

        {data?.headcount && (
          <Stats>
            <Stat label="Students" value={data.headcount.students} icon={<IconStudents />} />
            <Stat label="Teachers" value={data.headcount.teachers} tone="sky" icon={<IconTeacher />} />
            <Stat label="Staff" value={data.headcount.staff} tone="amber" icon={<IconStaff />} />
            <Stat
              label="Courses"
              value={data.headcount.courses}
              tone="neutral"
              icon={<IconCourses />}
            />
          </Stats>
        )}

        {(hasFees || hasRegistrations) && (
          <div className="dash__charts">
            {hasFees && (
              <Panel
                title={feesTitle}
                actions={
                  <MonthPicker
                    label="6 months ending"
                    value={feesPeriod}
                    max={currentPeriod}
                    onChange={setFeesPeriod}
                  />
                }
              >
                <ChartArea busy={refetching}>
                  <FeesByMonth months={fees!} />
                </ChartArea>
              </Panel>
            )}

            {hasRegistrations && (
              <Panel
                title={registrationsTitle}
                actions={
                  <select
                    className="select"
                    style={{ width: 110 }}
                    aria-label="Year"
                    value={registrationsYear}
                    onChange={(e) => setRegistrationsYear(Number(e.target.value))}
                  >
                    {registrationYearOptions.map((year) => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                }
              >
                <ChartArea busy={refetching}>
                  <RegistrationChart months={registrations!} periodLabel={registrationsPeriodLabel} />
                </ChartArea>
              </Panel>
            )}
          </div>
        )}

        {submissions && submissions.length > 0 && (
          <Panel
            title={submissionsTitle}
            actions={
              <MonthPicker
                label="Month"
                value={submissionsPeriod}
                max={currentPeriod}
                onChange={setSubmissionsPeriod}
              />
            }
          >
            <ChartArea busy={refetching}>
              <SubmissionsThisMonth days={submissions} periodLabel={submissionsPeriodLabel} />
            </ChartArea>
          </Panel>
        )}

        {TILE_GROUPS.map((group) => {
          const inGroup = tiles.filter((tile) => tile.group === group);
          if (inGroup.length === 0) return null;

          return (
            <div key={group}>
              {/* The tile groups keep their band. Unlike the charts these have no heading of
                  their own, and ten unlabelled cards in three silent clusters is exactly the
                  wall of links the grouping exists to break up. */}
              <SectionTitle>{group}</SectionTitle>
              <div className="tiles">
                {inGroup.map((tile) => {
                  const Icon = NAV_ICONS[tile.to] ?? IconArrowRight;

                  return (
                    <Link key={tile.to} to={tile.to} className="tile">
                      <span className="tile__icon">
                        <Icon />
                      </span>
                      <span className="tile__text">
                        <b>{tile.label}</b>
                        <span>{tile.hint}</span>
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}

        {tiles.length === 0 && !summary.loading && (
          <div className="card">
            <EmptyState
              title="Nothing assigned to your role"
              body="Your account can sign in, but no dashboard shortcuts apply to it. The menu on the left shows everything you can reach."
            />
          </div>
        )}
      </div>
    </>
  );
}
